// Shared measure-value formatting for the config-driven scheduler charts
// (TableSummary, ChartSummary). The stored chart config is a pure STWv3 config;
// its data endpoint returns a `formats` map of measure name -> format code. This
// turns a raw value + its format code into a display string so every chart
// formats a measure the same way.
//
// Kept in lock-step with the backend `format_value` in
// project/fds_utils/fds_format_utils.py — same codes, same output (notably the
// time family, which mirrors Python's `strftime(fmt, gmtime(abs(value)))`).

import {WEATHER_ICONS} from './_weatherIcons'

const num = (v: number, digits: number) =>
  new Intl.NumberFormat('en-US', {maximumFractionDigits: digits}).format(v)

// `weather_icon`: a condition string rendered as its emoji. A condition off the
// map shows as its own words rather than '-', so a provider rename reads as
// weather instead of a blank until the map is updated. This is the one place
// the UI parts from the backend's format_value, which still answers '-'.
// (Empty cells never reach here; formatMeasureValue draws their dash.)
//
// Matched case-SENSITIVELY, exactly as the Python dict lookup does. The map
// carries two providers' casings of the same weather, and on four of them the
// casings disagree about the icon — 'Light snow' is 🌨️❄️ while 'Light Snow' is
// 🌨️, and likewise for Blowing snow, Light rain shower and Heavy freezing
// drizzle. Folding case would silently pick one of each pair and put a
// different icon on screen than every other surface shows.
export const weatherIcon = (condition: string): string => WEATHER_ICONS[condition] ?? condition

// A weather cell with no reading in it. The summary endpoint is a numeric
// table and fills a day it has no weather for with 0, so 0 is "nothing
// recorded", not a condition — it draws the dash, never the text "0".
const isNoWeather = (v: number | string | null | undefined): boolean =>
  v === null || v === undefined || v === '' || v === 0 || v === '0'

// The hover text for a formatted cell, or undefined when the cell needs none.
//
// Only the weather formats have one, and they earn it: the icon is a lossy
// rendering of the value. ☁️ is Cloudy, Mostly Cloudy AND Overcast, so the cell
// is the one place the underlying condition can still be read; and a condition
// off the map is printed as-is, so the tooltip is what says it is unmapped
// rather than a value that simply has no icon.
export const measureValueTitle = (
  v: number | string | null | undefined,
  format?: string
): string | undefined => {
  if (format !== 'weather_icon') return undefined
  if (isNoWeather(v)) return 'No weather recorded'
  const condition = String(v)
  return WEATHER_ICONS[condition] ? condition : `${condition} (no icon for this condition)`
}

const pad = (n: number) => String(n).padStart(2, '0')

// gmtime(abs(seconds)) — struct fields in UTC from epoch(0) + |value| seconds.
// Python `time.gmtime` is UTC-based, so a UTC Date built from the same seconds
// yields the identical hour/minute/second/date fields.
const secParts = (value: number) => {
  const d = new Date(Math.abs(value) * 1000)
  return {
    H: d.getUTCHours(),
    M: d.getUTCMinutes(),
    S: d.getUTCSeconds(),
    mo: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    Y: d.getUTCFullYear(),
  }
}

// The seconds-based time formats (values are a number of seconds). Mirrors the
// `tme*` / `datetme*` branches of the backend `format_value`. Returns null for
// any code that is not a time format, so the caller falls through to numbers.
const formatTime = (v: number, format: string): string | null => {
  switch (format) {
    case 'tme': {
      // %M:%S with the leading zero of the minutes stripped ("01:17" -> "1:17").
      const {M, S} = secParts(v)
      let s = `${pad(M)}:${pad(S)}`
      if (s[0] === '0') s = s.slice(1)
      return (v < 0 ? '- ' : '') + s
    }
    case 'tme_from_sec':
    case 'tme_from_sec_w_hour': {
      // %H:%M
      const {H, M} = secParts(v)
      return `${pad(H)}:${pad(M)}`
    }
    case 'tme_from_sec_12h': {
      // %I:%M %p with the leading zero of the hour stripped.
      const {H, M} = secParts(v)
      const ampm = H >= 12 ? 'PM' : 'AM'
      const h12 = H % 12 === 0 ? 12 : H % 12
      let s = `${pad(h12)}:${pad(M)} ${ampm}`
      if (s[0] === '0') s = s.slice(1)
      return s
    }
    case 'tme_from_sec_full': {
      // %H:%M:%S
      const {H, M, S} = secParts(v)
      return `${pad(H)}:${pad(M)}:${pad(S)}`
    }
    case 'datetme_from_sec_full': {
      // %m/%d/%Y %H:%M:%S
      const {H, M, S, mo, day, Y} = secParts(v)
      return `${pad(mo)}/${pad(day)}/${Y} ${pad(H)}:${pad(M)}:${pad(S)}`
    }
    default:
      return null
  }
}

// STWv3 measure column titles carry a " (mm/dd/yy-mm/dd/yy)" date-range suffix;
// strip it for labels / series names.
export const stripSuffix = (col: string) => col.replace(/\s*\([^)]*\)\s*$/, '')

// Format a raw value by its measure format code — time (tme/tme_from_sec/...),
// dollars (dol0/dol2), percent (pct0/pct1), or number (num/num1/num_comma).
// Unknown/missing codes fall back to plain 2-decimal.
export const formatMeasureValue = (
  v: number | string | null | undefined,
  format?: string
): string => {
  if (v === null || v === undefined || v === '') return '-'
  // Before the non-number bail-out below: the weather formats are the only ones
  // whose input is text, and weather_icon has to translate it rather than pass
  // it through. weather_condition is deliberately absent — the backend passes
  // that one through unchanged, which String(v) already does.
  if (format === 'weather_icon') return isNoWeather(v) ? '-' : weatherIcon(String(v))
  if (typeof v !== 'number') return String(v)
  if (isNaN(v)) return '-'
  if (format) {
    const t = formatTime(v, format)
    if (t !== null) return t
  }
  const m = format?.match(/^(dol|pct|num)(\d)?(_comma)?$/)
  if (!m) return num(v, 2)
  const digits = m[2] !== undefined ? Number(m[2]) : m[1] === 'num' ? 1 : 0
  switch (m[1]) {
    case 'dol':
      return '$' + num(v, digits)
    case 'pct':
      return isFinite(v) ? num(v * 100, digits) + '%' : '-'
    default:
      return num(v, digits)
  }
}
